import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Modal, Select, Table } from 'antd'
import dayjs from 'dayjs'

export interface MissingZoneOrder {
  id: number
  order_reference: string | null
  delivery_date: string | null
  distribution_centre_name: string | null
  customer_location_address: string | null
  zone_id?: number | null
}

interface ZoneOption {
  id: number
  value: string
}

interface AssignZonesModalProps {
  isModalOpen: boolean
  orders: MissingZoneOrder[]
  zones: ZoneOption[]
  loading?: boolean
  onCancel: () => void
  onOk: (rows: Array<{ id: number; zone_id: number }>) => void
}

export const AssignZonesModal: React.FC<AssignZonesModalProps> = ({
  isModalOpen,
  orders,
  zones,
  loading = false,
  onCancel,
  onOk,
}) => {
  const [zoneByOrder, setZoneByOrder] = useState<Record<number, number>>({})

  useEffect(() => {
    if (!isModalOpen) {
      return
    }
    const initial: Record<number, number> = {}
    orders.forEach(order => {
      if (order.zone_id) {
        initial[order.id] = order.zone_id
      }
    })
    setZoneByOrder(initial)
  }, [isModalOpen, orders])

  const zoneOptions = useMemo(
    () => zones.map(zone => ({ value: zone.id, label: zone.value })),
    [zones],
  )

  const handleZoneChange = useCallback((orderId: number, value: number) => {
    setZoneByOrder(prev => ({
      ...prev,
      [orderId]: value,
    }))
  }, [])

  const isSubmitDisabled = useMemo(() => {
    if (orders.length === 0) {
      return true
    }
    return orders.some(order => !zoneByOrder[order.id])
  }, [orders, zoneByOrder])

  const handleOk = useCallback(() => {
    if (isSubmitDisabled) {
      return
    }
    const payload = orders.map(order => ({
      id: order.id,
      zone_id: zoneByOrder[order.id],
    }))
    onOk(payload)
  }, [isSubmitDisabled, onOk, orders, zoneByOrder])

  const columns = useMemo(
    () => [
      {
        title: '№ ТЗ',
        dataIndex: 'order_reference',
        key: 'order_reference',
        width: 120,
        render: (value: string | null, record: MissingZoneOrder) => (
          <a href={`/logistics/deliveries/${record.id}`} target="_blank" rel="noopener noreferrer">
            {value || record.id}
          </a>
        ),
      },
      {
        title: 'Дата доставки',
        dataIndex: 'delivery_date',
        key: 'delivery_date',
        width: 140,
        render: (value: string | null) =>
          value ? dayjs(value).format('DD.MM.YYYY') : '—',
      },
      {
        title: 'Адрес',
        dataIndex: 'customer_location_address',
        key: 'customer_location_address',
        ellipsis: true,
      },
      {
        title: 'Территория',
        key: 'zone_id',
        width: 220,
        render: (_: unknown, record: MissingZoneOrder) => (
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Выберите территорию"
            value={zoneByOrder[record.id]}
            onChange={value => handleZoneChange(record.id, value)}
            options={zoneOptions}
            style={{ width: '100%' }}
          />
        ),
      },
    ],
    [handleZoneChange, zoneByOrder, zoneOptions],
  )

  if (!isModalOpen) {
    return null
  }

  return (
    <Modal
      title="Территории не найдены"
      open={isModalOpen}
      onCancel={onCancel}
      onOk={handleOk}
      okText="Сохранить"
      cancelText="Закрыть"
      destroyOnHidden={true}
      okButtonProps={{ disabled: isSubmitDisabled, loading }}
      width={900}
    >
      <div style={{ marginBottom: 12 }}>
        Для этих заказов территория не пришла из Шедекс. Укажите ее вручную.
      </div>
      <Table
        rowKey="id"
        dataSource={orders}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ y: 360 }}
      />
    </Modal>
  )
}
